import React from 'react';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

/**
 * Read-only view of a bidder's `rtb_connection_info_map` - the per-region
 * connection overrides the ad server picks from at serve time.
 *
 * Read-only on purpose: the map is hand-maintained in Mongo (Bidder.java notes
 * the same about `endpoint_routing`), and 15 of the 106 entries in production
 * carry hand-parked keys like `OLD_connection_url` that a form binding would
 * either drop on the next save or promote into real fields. Ad ops still need
 * to SEE which region points where, so this renders the stored document as-is
 * and edits nothing.
 */

// The ad server's fallbacks when an entry omits a timeout, so the value shown
// is the one that actually applies. Bidder.java:318-322 (connection/request/
// read) and RTBConnectionInfo.getMaxConnections* (pool sizes).
export const CONNECTION_DEFAULTS = {
  connection_timeout: 500,
  request_timeout: 500,
  read_timeout: 1000,
  max_connections: 5,
  max_connections_per_host: 5
};

// Everything RTBConnectionInfo maps. Anything else in an entry is inert - the
// ad server never reads it - but it is shown rather than hidden, because that
// is exactly how an endpoint gets parked (`OLD_connection_url`).
export const KNOWN_ENTRY_FIELDS = [
  'connection_url',
  'connection_timeout',
  'request_timeout',
  'read_timeout',
  'connection_headers',
  'max_connections',
  'max_connections_per_host'
];

export const ROUTING_SERVING_REGION = 'SERVING_REGION';
export const ROUTING_VISITOR_REGION = 'VISITOR_REGION';

/**
 * Mirrors EndpointRouting.fromValue(): case-insensitive, and anything
 * unrecognized - including a missing field - is SERVING_REGION. A typo in
 * Mongo degrades to today's behaviour rather than to a broken bidder, and the
 * screen must say the same thing the ad server does.
 */
export function resolveEndpointRouting (raw) {
  if (typeof raw !== 'string') return ROUTING_SERVING_REGION;
  const trimmed = raw.trim().toUpperCase();
  return trimmed === ROUTING_VISITOR_REGION ? ROUTING_VISITOR_REGION : ROUTING_SERVING_REGION;
}

export const ROUTING_DESCRIPTIONS = {
  [ROUTING_SERVING_REGION]:
    'Keys are matched against the datacenter serving the request. A key that does not match falls through to the flat connection above.',
  [ROUTING_VISITOR_REGION]:
    "Keys are matched against the visitor's country (ISO-2, e.g. JP), then the region derived from it (e.g. apac), then the serving datacenter, then the flat connection above."
};

/**
 * Normalizes one map entry for display. Nothing is dropped: recognized fields
 * are laid out, and everything else comes back under `extras` verbatim.
 */
export function describeEntry (entry) {
  const source = (entry && typeof entry === 'object') ? entry : {};
  const url = typeof source.connection_url === 'string' ? source.connection_url.trim() : '';
  const headerMap = (source.connection_headers && typeof source.connection_headers === 'object')
    ? source.connection_headers
    : {};
  const extras = Object.keys(source)
    .filter(key => !KNOWN_ENTRY_FIELDS.includes(key))
    .map(key => ({ key, value: formatValue(source[key]) }));
  return {
    url,
    // An entry that exists but carries no URL does NOT fall back to the flat
    // connection - resolveConnection() returns the entry it found, and
    // BiddingServiceOpenRTB.getFutureResponse() returns null on an empty URL.
    // So the bidder is simply skipped wherever this key matches.
    skipsBidder: !url,
    numbers: ['connection_timeout', 'request_timeout', 'read_timeout', 'max_connections', 'max_connections_per_host']
      .map(field => ({
        field,
        value: Number.isFinite(source[field]) ? source[field] : CONNECTION_DEFAULTS[field],
        isDefault: !Number.isFinite(source[field])
      })),
    headers: Object.keys(headerMap).map(key => ({ key, value: formatValue(headerMap[key]) })),
    extras
  };
}

function formatValue (value) {
  if (value == null) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/**
 * Map -> ordered [key, description] pairs. Keys are sorted so the same bidder
 * reads the same way on every visit; Mongo gives no guaranteed key order.
 */
export function connectionMapEntries (map) {
  if (!map || typeof map !== 'object' || Array.isArray(map)) return [];
  return Object.keys(map).sort().map(key => ({ key, ...describeEntry(map[key]) }));
}

const NUMBER_LABELS = {
  connection_timeout: 'Connect',
  request_timeout: 'Request',
  read_timeout: 'Read',
  max_connections: 'Max conn',
  max_connections_per_host: 'Per host'
};

const useStyles = makeStyles(theme => ({
  // No hard-coded background: the console theme is dark, and a pinned light
  // panel with theme-coloured text renders as an unreadable bar.
  panel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    // The tab's grid column is half the viewport, which the four-column table
    // overruns - without this the rows spill straight through the panel's right
    // border instead of scrolling inside it.
    overflowX: 'auto'
  },
  table: { minWidth: 560 },
  cell: { verticalAlign: 'top' },
  url: { wordBreak: 'break-all' },
  muted: { color: theme.palette.text.secondary },
  warning: { color: theme.palette.warning.main },
  keyChip: { fontWeight: 600 },
  // Only the number pairs are short enough to keep on one line. A URL parked
  // under an unrecognized key is as long as a real one, so anything that can
  // hold a URL wraps - a nowrap here pushed the table past the panel and hid
  // the Timeouts and Headers columns behind a scrollbar.
  pair: { display: 'block', whiteSpace: 'nowrap' },
  // `anywhere` rather than break-all: it breaks at the space in
  // "Content-Type: application/json" before it splits a word mid-token.
  wrapPair: { display: 'block', overflowWrap: 'anywhere' }
}));

const NumberList = ({ numbers, classes }) => (
  <>
    {numbers.map(({ field, value, isDefault }) => (
      <Typography key={field} variant="caption" className={`${classes.pair} ${isDefault ? classes.muted : ''}`}>
        {NUMBER_LABELS[field]}: {value}{isDefault ? ' (default)' : ''}
      </Typography>
    ))}
  </>
);

/**
 * @param record the bidder record (reads `rtb_connection_info_map` and
 *   `endpoint_routing`; both may be absent).
 */
export const RtbConnectionMapField = ({ record }) => {
  const classes = useStyles();
  const entries = connectionMapEntries(record && record.rtb_connection_info_map);
  const routing = resolveEndpointRouting(record && record.endpoint_routing);

  if (!entries.length) {
    return (
      <Box className={classes.panel}>
        <Typography variant="body2" className={classes.muted}>
          No per-region connections configured - every request uses the connection above.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.panel}>
      <Typography variant="body2" gutterBottom>
        Endpoint routing: <strong>{routing}</strong>
      </Typography>
      <Typography variant="caption" className={classes.muted}>
        {ROUTING_DESCRIPTIONS[routing]}
      </Typography>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Key</TableCell>
            <TableCell>Connection URL</TableCell>
            <TableCell>Timeouts / Pool</TableCell>
            <TableCell>Headers</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(entry => (
            <TableRow key={entry.key}>
              <TableCell className={classes.cell}>
                <Chip size="small" label={entry.key} className={classes.keyChip} />
              </TableCell>
              <TableCell className={classes.cell}>
                {entry.skipsBidder ? (
                  <Typography variant="caption" className={classes.warning}>
                    No connection_url - this bidder is skipped wherever this key matches (it does NOT fall back to the connection above).
                  </Typography>
                ) : (
                  <Typography variant="caption" className={classes.url}>{entry.url}</Typography>
                )}
                {entry.extras.map(extra => (
                  <Typography key={extra.key} variant="caption" className={`${classes.wrapPair} ${classes.muted}`}>
                    {extra.key}: {extra.value} (ignored by the ad server)
                  </Typography>
                ))}
              </TableCell>
              <TableCell className={classes.cell}>
                <NumberList numbers={entry.numbers} classes={classes} />
              </TableCell>
              <TableCell className={classes.cell}>
                {entry.headers.length ? entry.headers.map(header => (
                  <Typography key={header.key} variant="caption" className={classes.wrapPair}>
                    {header.key}: {header.value}
                  </Typography>
                )) : (
                  <Typography variant="caption" className={classes.muted}>none</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default RtbConnectionMapField;
